import React, {
  useCallback, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/system';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import CheckIcon from '@mui/icons-material/Check';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PublicOffIcon from '@mui/icons-material/PublicOff';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import {
  diffListAgainstPublic, getListCoverImageUrl, getPrimaryLinkUrl, mergePublicAndPrivateItems,
  shareActionTitle, shareChangesState, shareResultMessage,
} from '../../common/ItemTools';
import { DELETE_LABEL, UNSHARE_LABEL } from '../../common/ActionLabels';
import { selectResolveShareTags } from '../../redux/features/tags/tagsSlice';
import {
  bookmarkList, deleteList, updateList, shareList,
} from '../../redux/features/lists/listsSlice';
import { bookmarkItem, fetchItemsByListPreview, shareItem } from '../../redux/features/items/itemsSlice';
import { setSnackbar } from '../../redux/features/snackbarSlice';
import { getNamespacePath } from '../../redux/features/context/contextSlice';
import useConfirm from '../../hooks/useConfirm';
import useGoBack from '../../hooks/useGoBack';
import FitItemGrid from '../FitItemGrid';
import listLogo from '../../resources/list.png';
import { TEXT_BODY, SERIF_STACK } from '../common/tokens';
import {
  AccentButton, ActionDivider, BackButton, DangerButton, PageBackground, HeroCard, SectionLabel, GroupLabel,
  OutlinedButton, PrimaryButton, TextInput, TextArea,
} from '../common/hero/styles';
import BookmarkToggle from '../common/hero/BookmarkToggle';
import FavoriteButton from '../common/hero/FavoriteButton';
import ShareButton from '../common/hero/ShareButton';
import HeroVerdictActions from '../common/hero/VerdictActions';
import useVerdict from '../common/hero/useVerdict';
import HeroIdentityHeader, { HeroAvatar } from '../common/hero/IdentityHeader';
import HeroFacts from '../common/hero/Facts';
import ViewAllLink from '../common/hero/ViewAllLink';
import ScopeControl from '../common/hero/ScopeControl';
import { factsLine, openUrlInNewTab } from '../common/hero/utils';
import { useFitGrid } from '../common/hero/useFitGrid';
import { useNamespacePreviews } from '../common/hero/useNamespacePreviews';
import { buildDraft, diffDraft } from './utils';

export default function ListHeroPage(props) {
  const {
    listInfo: rawListInfo, namespace, onRefetch, startInEdit,
  } = props;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const goBack = useGoBack();
  const { confirm, confirmDialog } = useConfirm();
  const userInfo = useSelector((state) => state.users.userInfo);

  const listInfo = useMemo(() => mergePublicAndPrivateItems(rawListInfo), [rawListInfo]);

  const resolveShareTags = useSelector(selectResolveShareTags);
  const shareDiff = useMemo(
    () => diffListAgainstPublic(rawListInfo, resolveShareTags(rawListInfo.privateData?.tagIds)),
    [rawListInfo, resolveShareTags],
  );

  const [editing, setEditing] = useState(Boolean(startInEdit));
  const [draft, setDraft] = useState(() => (startInEdit ? buildDraft(listInfo) : null));
  const [saving, setSaving] = useState(false);

  const isPending = namespace === 'pending';
  const editNamespace = isPending ? 'pending' : 'private';
  const scoped = Boolean(userInfo) && !isPending;

  const { decide, deciding } = useVerdict('list', rawListInfo.id, listInfo.title);

  // Measure the space under the identity header and fill it with square cards.
  const gridAreaRef = useRef(null);
  const { arrangement, pageSize } = useFitGrid(gridAreaRef, { deps: [editing] });

  const fetchPreview = useCallback((ns, limit) => dispatch(fetchItemsByListPreview({
    namespace: ns, listId: rawListInfo.id, params: { limit },
  })).unwrap(), [dispatch, rawListInfo.id]);

  const {
    previews, loaded: previewLoaded, activeNamespace, setScope,
    showScope, scopeOptions, recent: preview, refresh: refreshPreviews,
  } = useNamespacePreviews({
    entityId: rawListInfo.id, namespace, scoped, pageSize, fetchPreview,
  });

  const namespacePath = getNamespacePath(activeNamespace);

  // Edit lifecycle
  const enterEdit = () => {
    setDraft(buildDraft(listInfo));
    setEditing(true);
  };
  const cancelEdit = () => {
    setDraft(null);
    setEditing(false);
  };
  const setField = (field, value) => setDraft((d) => ({ ...d, [field]: value }));

  const reportError = (message) => dispatch(setSnackbar({
    snackbarOpen: true, snackbarType: 'error', snackbarMessage: message,
  }));

  const patchList = (patch) => dispatch(updateList({
    namespace: editNamespace, listId: rawListInfo.id, patch,
  })).unwrap();

  const saveEdit = async () => {
    setSaving(true);
    try {
      const patch = diffDraft(listInfo, draft);
      if (Object.keys(patch).length > 0) await patchList(patch);
      setEditing(false);
      setDraft(null);
      if (onRefetch) onRefetch();
    } catch (error) {
      reportError('Failed to save the list.');
    } finally {
      setSaving(false);
    }
  };

  const removeList = async () => {
    const ok = await confirm({
      title: 'Delete list?',
      message: `"${listInfo.title || 'This list'}" will be permanently removed. The items in it stay in your library.`,
      confirmLabel: DELETE_LABEL,
      destructive: true,
    });
    if (!ok) return;
    dispatch(deleteList({ namespace: 'private', listId: rawListInfo.id }))
      .unwrap()
      .then(goBack)
      .catch(() => reportError('Failed to delete the list.'));
  };

  const unshareList = async () => {
    const ok = await confirm({
      title: 'Unshare list?',
      message: `"${listInfo.title || 'This list'}" will be removed from the public record. Its creator keeps their private copy.`,
      confirmLabel: UNSHARE_LABEL,
      destructive: true,
    });
    if (!ok) return;
    dispatch(deleteList({ namespace: 'public', listId: rawListInfo.id }))
      .unwrap()
      .then(() => {
        if (listInfo.hasPrivateData) {
          if (onRefetch) onRefetch();
        } else {
          goBack();
        }
      })
      .catch(() => reportError('Failed to unshare the list.'));
  };

  const share = () => {
    dispatch(shareList({ listId: rawListInfo.id }))
      .unwrap()
      .then((result) => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'success',
          snackbarMessage: shareResultMessage(result),
        }));
        if (onRefetch) onRefetch();
      })
      .catch(() => reportError('Failed to share the list.'));
  };

  const toggleBookmark = async () => {
    if (rawListInfo.privateData) {
      const ok = await confirm({
        title: 'Remove from your library?',
        message: `Your private copy of "${listInfo.title || 'this list'}" is deleted, along with any changes you made to it. The items in it stay in your library, and the public list stays.`,
        confirmLabel: 'Remove',
        destructive: true,
      });
      if (!ok) return;
      dispatch(deleteList({ namespace: 'private', listId: rawListInfo.id }))
        .unwrap()
        .then(() => {
          refreshPreviews();
          if (onRefetch) onRefetch();
        })
        .catch(() => reportError('Failed to remove the private copy.'));
      return;
    }

    dispatch(bookmarkList({ listId: rawListInfo.id }))
      .unwrap()
      .then(() => onRefetch && onRefetch())
      .catch(() => reportError('Failed to bookmark the list.'));
  };

  const shareItemFromCard = (it) => dispatch(shareItem({ itemId: it.id }))
    .unwrap()
    .then((result) => {
      dispatch(setSnackbar({
        snackbarOpen: true, snackbarType: 'success', snackbarMessage: shareResultMessage(result),
      }));
      return refreshPreviews();
    })
    .catch(() => reportError('Failed to share the item.'));

  const bookmarkItemFromCard = (it) => dispatch(bookmarkItem({ itemId: it.id }))
    .unwrap()
    .then(refreshPreviews)
    .catch(() => reportError('Failed to bookmark item.'));

  // Favorite persists immediately.
  const toggleFavorite = () => {
    patchList({ isFavorite: !listInfo.isFavorite })
      .then(() => { if (onRefetch) onRefetch(); })
      .catch(() => reportError('Failed to favorite the list.'));
  };

  const itemCardActions = {
    onItemRead: (it) => navigate(isPending ? `/moderate/items/${it.id}` : `/items/${it.id}`),
    onItemOpen: (it) => openUrlInNewTab(getPrimaryLinkUrl(it)),
    onTagOpen: (tagId) => navigate(`/${namespacePath}/items?tag=${tagId}`),
    onAuthorView: (authorId) => navigate(`/authors/${authorId}`),
    onAuthorFilter: (authorId) => navigate(`/${namespacePath}/items?author=${authorId}`),
    onSiteView: (siteId) => navigate(`/sites/${siteId}`),
    onSiteFilter: (siteId) => navigate(`/${namespacePath}/items?site=${siteId}`),
    ...(scoped && {
      onItemShare: shareItemFromCard,
      onItemBookmark: bookmarkItemFromCard,
    }),
  };

  // Derived identity (read mode reads the list; edit mode reflects the draft
  // live so the header updates as the user types).
  const title = editing && draft ? draft.title : listInfo.title;
  const description = editing && draft ? draft.description : listInfo.description;
  const coverUrl = (editing && draft ? draft.coverImageUrl : getListCoverImageUrl(listInfo)) || '';
  const isFavorite = Boolean(listInfo.isFavorite);
  const countsPending = !editing && !previewLoaded;
  const hasPrivate = Boolean(rawListInfo.privateData);
  const hasPublic = Boolean(rawListInfo.publicData);
  // Only the namespace on show is quoted — the scope control beside the line
  // is what switches it.
  const activeData = isPending ? listInfo : rawListInfo[`${activeNamespace}Data`];
  const facts = factsLine(activeData, previews[activeNamespace]?.itemsCount, activeNamespace);

  const canBookmark = scoped && hasPublic;
  const canDelete = !isPending && hasPrivate && !hasPublic;
  const canUnshare = !isPending && hasPublic && Boolean(userInfo?.isModerator);
  const canShare = !isPending && hasPrivate;
  const canDecide = isPending && Boolean(userInfo?.isModerator);

  const readActions = !editing && userInfo ? (
    <>
      {!isPending && (
        <>
          <FavoriteButton isFavorite={isFavorite} onToggle={toggleFavorite} />
          {canBookmark && (
            <BookmarkToggle hasPrivateData={hasPrivate} onToggle={toggleBookmark} />
          )}
          {canShare && (
            <ShareButton
              hasPublicData={hasPublic}
              changes={shareChangesState(shareDiff)}
              title={shareActionTitle(hasPublic, shareDiff)}
              onShare={share}
            />
          )}
          <ActionDivider />
        </>
      )}
      {canUnshare && (
        <DangerButton type="button" onClick={unshareList}>
          <PublicOffIcon sx={{ fontSize: 16 }} />
          {UNSHARE_LABEL}
        </DangerButton>
      )}
      {canDelete && (
        <DangerButton type="button" onClick={removeList}>
          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
          {DELETE_LABEL}
        </DangerButton>
      )}
      <AccentButton type="button" onClick={enterEdit}>
        <EditOutlinedIcon sx={{ fontSize: 16 }} />
        Edit
      </AccentButton>
      {canDecide && (
        <>
          <ActionDivider />
          <HeroVerdictActions onDecide={decide} disabled={deciding} />
        </>
      )}
    </>
  ) : null;
  const headerActions = editing ? (
    <>
      <OutlinedButton type="button" onClick={cancelEdit} disabled={saving}>Cancel</OutlinedButton>
      <PrimaryButton type="button" onClick={saveEdit} disabled={saving}>
        <CheckIcon sx={{ fontSize: 16 }} />
        Save
      </PrimaryButton>
    </>
  ) : readActions;

  // Never render more than the current page holds, so a shrink-resize can't
  // briefly overflow the grid with stale items before the refetch lands.
  const displayItems = pageSize ? preview.items.slice(0, pageSize) : preview.items;
  const itemCount = displayItems.length;
  const displayArrangement = arrangement && itemCount > 0 && itemCount <= arrangement.cols
    ? { cols: Math.max(2, itemCount), rows: 1 }
    : arrangement;

  return (
    <PageBackground sx={{ padding: 0 }}>
      <HeroCard sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          padding: '16px 28px 0',
          width: '100%',
          maxWidth: 1400,
          mx: 'auto',
          flexShrink: 0,
        }}>
          <BackButton type="button" onClick={goBack}>
            <ChevronLeftIcon sx={{ fontSize: 18 }} />
            <span>Back</span>
          </BackButton>
          <Box sx={{ flex: 1 }} />
          {headerActions && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {headerActions}
            </Box>
          )}
        </Box>

        {/* Content column — scrolls internally when the content doesn't fit,
            keeping the action bar pinned */}
        <Box sx={{
          width: '100%',
          maxWidth: 1232,
          mx: 'auto',
          padding: '24px 28px 24px',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
        }}>
          <HeroIdentityHeader
            avatar={<HeroAvatar imageUrl={coverUrl || listLogo} fit="contain" />}
            title={title || 'Untitled list'}
          />
          {!editing && description && (
            <Box sx={{
              flexShrink: 0,
              fontFamily: SERIF_STACK,
              fontSize: '1.05rem',
              lineHeight: 1.6,
              color: TEXT_BODY,
              mt: '18px',
              whiteSpace: 'pre-wrap',
            }}>
              {description}
            </Box>
          )}

          {/* Edit form (edit mode) */}
          {editing && draft && (
            <Box sx={{ mt: '24px', flexShrink: 0 }}>
              <SectionLabel>List details</SectionLabel>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <Box>
                  <GroupLabel>Title</GroupLabel>
                  <TextInput
                    style={{ width: '100%' }}
                    value={draft.title}
                    onChange={(e) => setField('title', e.target.value)}
                  />
                </Box>
                <Box>
                  <GroupLabel>Cover URL</GroupLabel>
                  <TextInput
                    style={{ width: '100%' }}
                    value={draft.coverImageUrl}
                    onChange={(e) => setField('coverImageUrl', e.target.value)}
                  />
                </Box>
                <Box>
                  <GroupLabel>Description</GroupLabel>
                  <TextArea
                    style={{ width: '100%', minHeight: 96 }}
                    value={draft.description}
                    onChange={(e) => setField('description', e.target.value)}
                  />
                </Box>
              </Box>
            </Box>
          )}

          {/* List items (read mode): fills the rest of the page height, cards
              fit the space like the item views. */}
          {!editing && (
            <Box
              ref={gridAreaRef}
              sx={{
                flex: 1,
                minHeight: 240,
                display: 'flex',
                flexDirection: 'column',
                mt: '18px',
              }}
            >
              {displayItems.length > 0 && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  minHeight: 30,
                  gap: '14px',
                  flexShrink: 0,
                }}>
                  <SectionLabel sx={{ mb: 0 }}>List items</SectionLabel>
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0,
                  }}>
                    <HeroFacts facts={facts} loading={countsPending} />
                    {showScope && (
                      <ScopeControl
                        options={scopeOptions}
                        value={activeNamespace}
                        onChange={setScope}
                      />
                    )}
                    <ViewAllLink
                      title={`View all ${preview.itemsCount} items in ${title}`}
                      onClick={() => navigate(`/${namespacePath}/items?list=${rawListInfo.id}`)}
                    />
                  </Box>
                </Box>
              )}
              {displayItems.length > 0 && (
                <FitItemGrid
                  items={displayItems}
                  namespace={activeNamespace}
                  itemActions={itemCardActions}
                  arrangement={displayArrangement}
                  isPreview
                />
              )}
            </Box>
          )}
        </Box>
      </HeroCard>

      {confirmDialog}
    </PageBackground>
  );
}

ListHeroPage.propTypes = {
  listInfo: PropTypes.object.isRequired,
  namespace: PropTypes.string,
  onRefetch: PropTypes.func,
  startInEdit: PropTypes.bool,
};

ListHeroPage.defaultProps = {
  namespace: 'public',
  onRefetch: null,
  startInEdit: false,
};
